// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    Box,
    SpaceBetween,
    FormField,
    Input,
    Checkbox,
    Button,
} from '@cloudscape-design/components';

/**
 * CustomFiltersModal component
 * Modal dialog for configuring custom resource filters
 * 
 * Allows users to:
 * - Exclude resources by prefix patterns
 * - Include resources by keyword patterns
 * - Toggle auto-generated resource hiding
 */
function CustomFiltersModal({
    visible,
    onDismiss,
    onApply,
    excludePrefixes,
    keywordFilter,
    hideAutoGenerated,
    onExcludePrefixesChange,
    onKeywordFilterChange,
    onHideAutoGeneratedChange,
    onClearAll,
}) {
    return (
        <Modal
            visible={visible}
            onDismiss={onDismiss}
            header="Custom Filters"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            variant="link"
                            onClick={onClearAll}
                        >
                            Clear all
                        </Button>
                        <Button
                            variant="link"
                            onClick={onDismiss}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={onApply}
                        >
                            Apply
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="l">
                <FormField
                    label="Exclude prefixes"
                    description="Comma-separated list of resource name prefixes to exclude (e.g., aws-, default-)"
                >
                    <Input
                        value={excludePrefixes}
                        onChange={({detail}) => onExcludePrefixesChange(detail.value)}
                        placeholder="aws-, default-, temp-"
                        ariaLabel="Exclude resource prefixes"
                    />
                </FormField>

                <FormField
                    label="Include keywords"
                    description="Comma-separated list of keywords to include in resource names"
                >
                    <Input
                        value={keywordFilter}
                        onChange={({detail}) => onKeywordFilterChange(detail.value)}
                        placeholder="prod, api, database"
                        ariaLabel="Include resource keywords"
                    />
                </FormField>

                <Checkbox
                    checked={hideAutoGenerated}
                    onChange={({detail}) => onHideAutoGeneratedChange(detail.checked)}
                >
                    Hide auto-generated resources
                </Checkbox>

            </SpaceBetween>
        </Modal>
    );
}

CustomFiltersModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    excludePrefixes: PropTypes.string.isRequired,
    keywordFilter: PropTypes.string.isRequired,
    hideAutoGenerated: PropTypes.bool.isRequired,
    onExcludePrefixesChange: PropTypes.func.isRequired,
    onKeywordFilterChange: PropTypes.func.isRequired,
    onHideAutoGeneratedChange: PropTypes.func.isRequired,
    onClearAll: PropTypes.func.isRequired,
};

export default CustomFiltersModal;
